import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Wifi, WifiOff, ScanLine, Camera, RefreshCw, Play, Pause, CheckCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const JOBS_KEY = "floor_jobs";
const Q_KEY = "floor_queue";
const read = (k, d) => JSON.parse(localStorage.getItem(k) || JSON.stringify(d));
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));

function applyLocal(jobs, ev) {
  return jobs.map((j) => j.id !== ev.job_id ? j : { ...j, steps: j.steps.map((s) => {
    if (s.id !== ev.step_id) return s;
    if (ev.type === "start") return { ...s, status: "in_progress" };
    if (ev.type === "stop") return { ...s, status: "paused" };
    if (ev.type === "complete") return { ...s, status: "done", qa_status: s.qa_gate ? "pending" : null };
    if (ev.type === "qty") return { ...s, qty_produced: s.qty_produced + ev.value };
    if (ev.type === "spoilage") return { ...s, spoilage: s.spoilage + ev.value };
    return s; }) });
}

export default function Floor() {
  const { user } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const [jobs, setJobs] = useState(read(JOBS_KEY, []));
  const [queue, setQueue] = useState(read(Q_KEY, []));
  const [rejected, setRejected] = useState([]);
  const [code, setCode] = useState("");
  const [sel, setSel] = useState(null);
  const [val, setVal] = useState("");
  const [note, setNote] = useState("");
  const [cam, setCam] = useState(false);
  const video = useRef(null);
  const syncing = useRef(false);

  const pull = useCallback(async () => {
    try { const { data } = await api.get("/floor/active"); const local = read(Q_KEY, []).reduce(applyLocal, data); setJobs(local); write(JOBS_KEY, local); } catch { /* offline: keep cache */ }
  }, []);
  const sync = useCallback(async () => {
    const q = read(Q_KEY, []);
    if (!q.length || syncing.current || !navigator.onLine) return;
    syncing.current = true;
    try {
      const { data } = await api.post("/floor/sync", { events: q });
      const done = new Set(data.results.map((r) => r.client_id));
      const left = read(Q_KEY, []).filter((e) => !done.has(e.client_id));
      write(Q_KEY, left); setQueue(left);
      setRejected((r) => [...r, ...data.results.filter((x) => !x.ok)]);
      await pull();
    } catch { /* retry later */ } finally { syncing.current = false; }
  }, [pull]);

  useEffect(() => {
    const on = () => { setOnline(true); sync(); }; const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    pull().then(sync);
    const t = setInterval(sync, 15000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); clearInterval(t); };
  }, [pull, sync]);

  const lookup = (raw) => {
    const v = raw.trim();
    const m = v.match(/^PO:([^:]+):(.+)$/);
    if (m) { const j = jobs.find((x) => x.id === m[1]); const s = j?.steps.find((x) => x.id === m[2]); if (s) return setSel({ job: j, stepId: s.id }); }
    const j = jobs.find((x) => x.number.toLowerCase() === v.toLowerCase());
    if (j) return setSel({ job: j, stepId: (j.steps.find((s) => s.status !== "done") || j.steps[0]).id });
    setRejected((r) => [...r, { client_id: v, error: "Code not found in cached jobs" }]);
  };

  const record = (type, value) => {
    const ev = { client_id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, job_id: sel.job.id, step_id: sel.stepId, type, value, note: note || null, operator: user?.name, at: new Date().toISOString() };
    const q = [...read(Q_KEY, []), ev]; write(Q_KEY, q); setQueue(q);
    const nj = applyLocal(jobs, ev); setJobs(nj); write(JOBS_KEY, nj);
    setVal(""); setNote(""); sync();
  };

  const scanCamera = async () => {
    if (!("BarcodeDetector" in window)) { setRejected((r) => [...r, { client_id: "camera", error: "Camera scanning not supported on this browser; use a handheld scanner or type the code" }]); return; }
    setCam(true);
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
    video.current.srcObject = stream; await video.current.play();
    const det = new window.BarcodeDetector({ formats: ["qr_code", "code_128", "code_39"] });
    const tick = async () => {
      const r = await det.detect(video.current).catch(() => []);
      if (r.length) { stream.getTracks().forEach((t) => t.stop()); setCam(false); lookup(r[0].rawValue); } else if (stream.active) requestAnimationFrame(tick);
    };
    tick();
  };

  const job = sel && jobs.find((j) => j.id === sel.job.id);
  const step = job?.steps.find((s) => s.id === sel.stepId);
  return (
    <div className="min-h-screen bg-[#13294B] text-white">
      <header className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3"><Link to="/app/jobs" aria-label="Back to jobs" data-testid="floor-back-link" className="-ml-2 inline-flex h-11 w-11 items-center justify-center rounded-md text-slate-300 hover:bg-white/10"><ArrowLeft className="h-5 w-5" /></Link><span className="text-lg font-semibold">Shop floor</span></div>
        <div className="flex items-center gap-3 text-sm">
          <span data-testid="floor-online-status" className={`flex items-center gap-1 rounded-full px-2 py-0.5 ${online ? "bg-[#45BFB4]/20 text-[#45BFB4]" : "bg-orange-500/20 text-orange-300"}`}>{online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}{online ? "Online" : "Offline"}</span>
          <span data-testid="floor-queue-count">{queue.length} queued</span>
          <button data-testid="floor-sync-button" aria-label="Sync now" className="inline-flex h-11 w-11 items-center justify-center rounded-md hover:bg-white/10" onClick={() => { sync(); pull(); }}><RefreshCw className="h-4 w-4" /></button>
        </div>
      </header>
      <main className="mx-auto max-w-xl space-y-4 p-4">
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(code); setCode(""); }}>
          <Input autoFocus data-testid="floor-scan-input" placeholder="Scan QR / barcode or type job number" value={code} onChange={(e) => setCode(e.target.value)} className="h-12 bg-white text-slate-900" />
          <Button type="submit" data-testid="floor-scan-submit" className="h-12 bg-[#45BFB4] text-[#13294B]"><ScanLine className="h-5 w-5" /></Button>
          <Button type="button" data-testid="floor-camera-button" onClick={scanCamera} className="h-12 bg-white/10"><Camera className="h-5 w-5" /></Button>
        </form>
        {cam && <video ref={video} className="w-full rounded-lg" muted playsInline />}
        {!cam && <video ref={video} className="hidden" muted playsInline />}
        {job && step && (
          <div className="space-y-3 rounded-xl bg-white p-4 text-slate-900" data-testid="floor-step-panel">
            <div className="text-xs uppercase tracking-wider text-slate-500">{job.number} · {job.customer_name}</div>
            <div className="text-lg font-semibold">{job.title}</div>
            <select data-testid="floor-step-select" className="po-select !h-11" value={sel.stepId} onChange={(e) => setSel({ ...sel, stepId: e.target.value })}>
              {job.steps.map((s) => <option key={s.id} value={s.id}>{s.seq}. {s.name} — {s.status.replace("_", " ")}{s.qa_gate ? ` (QA ${s.qa_status || "gate"})` : ""}</option>)}</select>
            <div className="text-sm text-slate-500" data-testid="floor-step-status">Status: <b className="text-slate-900">{step.status.replace("_", " ")}</b> · produced {step.qty_produced} · spoilage {step.spoilage}</div>
            <div className="grid grid-cols-3 gap-2">
              <Button data-testid="floor-start-button" disabled={step.status === "in_progress" || step.status === "done"} onClick={() => record("start")} className="h-14 bg-[#45BFB4] text-[#13294B]"><Play className="mr-1 h-5 w-5" />Start</Button>
              <Button data-testid="floor-stop-button" disabled={step.status !== "in_progress"} onClick={() => record("stop")} className="h-14" variant="outline"><Pause className="mr-1 h-5 w-5" />Pause</Button>
              <Button data-testid="floor-complete-button" disabled={step.status !== "in_progress"} onClick={() => record("complete")} className="h-14 bg-[#13294B]"><CheckCheck className="mr-1 h-5 w-5" />Done</Button>
            </div>
            <div className="flex gap-2"><Input type="number" inputMode="numeric" data-testid="floor-qty-input" placeholder="Quantity" value={val} onChange={(e) => setVal(e.target.value)} className="h-12" />
              <Button data-testid="floor-produced-button" disabled={!val} onClick={() => record("qty", Number(val))} className="h-12" variant="outline">+ Produced</Button>
              <Button data-testid="floor-spoilage-button" disabled={!val} onClick={() => record("spoilage", Number(val))} className="h-12" variant="outline">+ Spoilage</Button></div>
            <div className="flex gap-2"><Input data-testid="floor-note-input" placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} className="h-12" />
              <Button data-testid="floor-note-button" disabled={!note} onClick={() => record("note")} className="h-12" variant="outline">Add note</Button></div>
          </div>
        )}
        {rejected.length > 0 && <div className="rounded-lg bg-rose-500/15 p-3 text-sm text-rose-200" data-testid="floor-rejected">{rejected.slice(-5).map((r, i) => <div key={i}>{r.error}</div>)}</div>}
        <div className="space-y-2" data-testid="floor-job-list">
          <div className="text-xs uppercase tracking-wider text-slate-400">Active jobs (cached for offline)</div>
          {jobs.map((j) => <button key={j.id} data-testid={`floor-job-${j.number}`} onClick={() => lookup(j.number)} className="flex w-full items-center justify-between rounded-lg bg-white/5 px-4 py-3 text-left hover:bg-white/10">
            <span><b>{j.number}</b> <span className="text-slate-300">{j.title}</span></span><span className="text-xs capitalize text-[#45BFB4]">{j.status.replace("_", " ")} · {j.steps.filter((s) => s.status === "done").length}/{j.steps.length}</span></button>)}
        </div>
      </main>
    </div>
  );
}
